/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Api } from "../../services/interceptor";
import { QUERY_KEY } from "../../services/queryEndPoint";
import { IcDn, IcShare, IcTick } from "./icons";
import { colorFor } from "./matchVm";
import type { MatchVm } from "./matchVm";

type TabDef = { key: string; label: string };
type Props = {
  tournament?: any; tournamentId: string; tournamentName: string; leagueName?: string | null; leagueId?: string | null;
  siblings: any[]; tabs: TabDef[]; activeTab: string; onTab: (k: string) => void; all: MatchVm[]; onLeagueHome: () => void;
};

const fmtRange = (s?: string, e?: string) => {
  if (!s) return "";
  const a = new Date(s), b = e ? new Date(e) : null;
  const o: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" };
  const y = (d: Date) => d.getFullYear();
  if (!b || Number.isNaN(b.getTime())) return `${a.toLocaleDateString("en-GB", { ...o, year: "numeric" })}`;
  return a.getMonth() === b.getMonth() && y(a) === y(b) ? `${a.getDate()}–${b.toLocaleDateString("en-GB", { ...o, year: "numeric" })}` : `${a.toLocaleDateString("en-GB", o)} – ${b.toLocaleDateString("en-GB", { ...o, year: "numeric" })}`;
};
const formatName = (t?: string) => (t === "TEST_MATCH" ? "Multi Day" : t === "LIMITED_OVERS" ? "Limited Overs" : t === "THE_HUNDRED" ? "The Hundred" : t ?? "");
const nameOf = (p: any) => (p ? [p.playerFirstName, p.playerLastName].filter(Boolean).join(" ") : "");

const useLeader = (tournamentId: string, filter: "BATTING" | "BOWLING", enabled: boolean) =>
  useQuery({
    queryKey: ["tpLeader", tournamentId, filter], enabled: enabled && !!tournamentId, staleTime: 60_000,
    queryFn: async () => (await Api.get(`${QUERY_KEY.tournamentLeaderboard}?tournamentId=${encodeURIComponent(tournamentId)}&filter=${filter}`)).data as any,
  });

const TournamentHero = ({ tournament, tournamentId, tournamentName, leagueName, siblings, tabs, activeTab, onTab, all, onLeagueHome }: Props) => {
  const nav = useNavigate();
  const [menu, setMenu] = useState<"hero" | "mini" | null>(null);
  const [stuck, setStuck] = useState(false);
  const h1 = useRef<HTMLHeadingElement>(null);
  const played = all.filter((m) => m.bucket === "past").length;
  const live = all.filter((m) => m.bucket === "live").length;
  const grounds = new Set(all.map((m) => m.ground).filter(Boolean)).size;
  const bat = useLeader(tournamentId, "BATTING", played > 0), bowl = useLeader(tournamentId, "BOWLING", played > 0);
  const topBat = useMemo(() => (bat.data?.battingStats ?? []).slice().sort((x: any, y: any) => (y.totalRunsScored ?? 0) - (x.totalRunsScored ?? 0))[0], [bat.data]);
  const topBowl = useMemo(() => (bowl.data?.bowlingStats ?? []).slice().sort((x: any, y: any) => (y.totalWickets ?? 0) - (x.totalWickets ?? 0))[0], [bowl.data]);
  const highest = useMemo(() => {
    let best: { runs: number; wkts: number; team: string } | null = null;
    all.filter((m) => m.bucket !== "up").forEach((m) => (m.raw.inningScorecardList ?? []).forEach((i: any) => { if (!best || i.runs > best.runs) best = { runs: i.runs, wkts: i.wicket, team: i.battingTeamName }; }));
    return best as { runs: number; wkts: number; team: string } | null;
  }, [all]);

  const now = Date.now();
  const start = tournament?.startDate ? new Date(tournament.startDate).getTime() : 0, end = tournament?.endDate ? new Date(tournament.endDate).getTime() + 86400000 : 0;
  const total = all.length;
  const badge = live ? <span className="badge prog"><span className="ldot on" />LIVE NOW</span>
    : !total ? <span className="badge">FIXTURES COMING SOON</span>
    : start && now < start ? <span className="badge">STARTS {new Date(start).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }).toUpperCase()}</span>
    : end && now > end || (played === total && total > 0) ? <span className="badge">COMPLETED</span>
    : <span className="badge prog">IN PROGRESS</span>;
  const none = <small>Available after the first match</small>;

  useEffect(() => {
    const el = h1.current; if (!el) return;
    const io = new IntersectionObserver(([e]) => setStuck(!e.isIntersecting), { rootMargin: "-90px 0px 0px 0px" });
    io.observe(el); return () => io.disconnect();
  }, []);
  useEffect(() => {
    const close = () => setMenu(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  const initials = (tournamentName || "T").replace(/^\d{4}\s*/, "").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const share = async () => {
    try { if (navigator.share) await navigator.share({ title: tournamentName, url: window.location.href }); else { await navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); } } catch { /* cancelled */ }
  };
  const Switcher = () => (
    <div className="lmenu" role="menu" style={{ position: "absolute", top: 46, left: 0 }} onClick={(e) => e.stopPropagation()}>
      <div className="g">Tournaments in {leagueName ?? "this league"}</div>
      {siblings.map((t) => {
        const on = t.tournamentId === tournamentId;
        return (
          <button key={t.tournamentId} role="menuitem" className={on ? "on" : ""} onClick={() => { setMenu(null); if (!on) nav(`/tournamentDetails/${t.tournamentId}`); }}>
            <span className="lgx" style={{ background: colorFor(t.tournamentName) }}>{(t.tournamentName ?? "T").replace(/^\d{4}\s*/, "").slice(0, 2).toUpperCase()}</span>
            <span><b>{t.tournamentName}</b><span className="m">{[fmtRange(t.startDate, t.endDate), formatName(t.matchType)].filter(Boolean).join(" · ")}</span></span>
            {on && <span className="ck"><IcTick /></span>}
          </button>
        );
      })}
      <hr /><button className="all" onClick={() => { setMenu(null); onLeagueHome(); }}>See all tournaments</button>
    </div>
  );

  return (
    <>
      <div className="tp tp-hero">
      <section className="hero">
        <div className="hbg" aria-hidden="true"><span className="halo" /><span className="tile" /><span className="scrim-l" /><span className="scrim-b" /><span className="grain" /></div>
        <div className="wrap" style={{ paddingTop: 18 }}>
          <nav className="crumb" aria-label="Breadcrumb"><a href="#" onClick={(e) => { e.preventDefault(); onLeagueHome(); }}>{leagueName ?? "League"}</a><span>/</span><a href="#" onClick={(e) => { e.preventDefault(); onLeagueHome(); }}>Tournaments</a><span>/</span><b>{tournamentName}</b></nav>
          <div className="hgrid">
            <div>
              <div className="meta">{badge}{fmtRange(tournament?.startDate, tournament?.endDate)}{tournament?.matchType && <><i />{formatName(tournament.matchType)}</>}{tournament?.maxOvers && tournament.matchType !== "TEST_MATCH" && !(tournament.scheduledDays > 1) ? <><i />{tournament.maxOvers} overs</> : null}</div>
              <h1 ref={h1}>{tournamentName}</h1>
            </div>
            <div className="hacts">
              {siblings.length > 1 && <div className="lsw" style={{ position: "relative" }}><button className="hbtn" aria-haspopup="menu" aria-expanded={menu === "hero"} onClick={(e) => { e.stopPropagation(); setMenu(menu === "hero" ? null : "hero"); }}>Switch tournament<IcDn /></button>{menu === "hero" && <Switcher />}</div>}
              <button className="hbtn solid" onClick={share}><IcShare />Share</button>
            </div>
          </div>
          <div className="facts">
            <div><div className="k">Matches</div><div className="v">{total ? <><span className="n">{played}</span> <small>of {total} played</small></> : <small>Fixtures not published yet</small>}</div></div>
            <div><div className="k">Grounds</div><div className="v">{grounds ? <span className="n">{grounds}</span> : <small>To be announced</small>}</div></div>
            <div><div className="k">Most runs</div><div className="v">{topBat ? <>{nameOf(topBat.batsman) || "–"} <small>· <span className="n">{topBat.totalRunsScored}</span></small></> : none}</div></div>
            <div><div className="k">Most wickets</div><div className="v">{topBowl ? <>{nameOf(topBowl.bowler) || "–"} <small>· <span className="n">{topBowl.totalWickets}</span></small></> : none}</div></div>
            <div><div className="k">Highest total</div><div className="v">{highest ? <><span className="n">{highest.runs}/{highest.wkts}</span> <small>{highest.team}</small></> : none}</div></div>
          </div>
        </div>
      </section>
      </div>
      <div className="tp tp-sticky">
      <div className="tabsbar" id="tp-tabsbar">
        <div className="wrap">
          <div style={{ position: "relative" }}>
            <button className={`mini ${stuck ? "on" : ""}`} aria-haspopup="menu" aria-expanded={menu === "mini"} tabIndex={stuck ? 0 : -1} onClick={(e) => { e.stopPropagation(); if (siblings.length > 1) setMenu(menu === "mini" ? null : "mini"); }}>
              <span className="lg">{initials}</span>{tournamentName}{siblings.length > 1 && <IcDn />}
            </button>
            {menu === "mini" && <Switcher />}
          </div>
          <div className="tabs" role="tablist" aria-label="Tournament sections">
            {tabs.map((t) => (<button key={t.key} className="tab" role="tab" aria-selected={activeTab === t.key} onClick={() => onTab(t.key)}>{t.label}</button>))}
          </div>
        </div>
      </div>
      </div>
    </>
  );
};
export default TournamentHero;
